import { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Button from "../../common/Button";
import Confirm from "../../common/Confirm";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../common/lib/KaAlert";
import QueryView from "../../common/lib/QueryView";
import Row from "../../common/Row";
import { adminFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import { UpsertSiteUser } from "../../requests/useUpsertSiteUser";
import useUpsertSiteUser from "../../requests/useUpsertSiteUser";
import { SiteUserDto, useGetSiteUsers } from "../../requests/useGetSiteUsers";
import useRemoveSiteUser from "../../requests/useRemoveSiteUser";

interface UserModalProps {
  user?: SiteUserDto;
  onSuccess: () => void;
  close: () => void;
}

const UserModal = ({ user, onSuccess, close }: UserModalProps) => {
  const isUpdate = !!user;
  const { register, handleSubmit, watch, formState: { errors } } = useForm<UpsertSiteUser>({
    defaultValues: {
      email: user?.email || '',
      role: user?.role || 'Admin'
    }
  });
  const role = watch('role');
  const onSubmit: SubmitHandler<UpsertSiteUser> = (data) => upsertUser.request(data);
  const displayMessage = useDisplayMessage();

  const upsertUser = useUpsertSiteUser({
    onSuccess,
    onError: (err) => displayMessage.fail(`Failed to ${isUpdate ? 'update' : 'add'} user: ${adminFacingErrorMessage(err)}`)
  });

  return (
    <>
      <div className="modal show" style={{ display: 'block' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">{isUpdate ? 'Update User' : 'Add New User'}</h2>
              <button type="button" className="btn-close" onClick={close} />
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body pb-0">
                <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />

                <div className="mb-3">
                  <label htmlFor="email" className="form-label">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    className={`form-control ${errors.email && !isUpdate ? 'is-invalid' : ''}`}
                    {...register("email", {
                      required: isUpdate ? false : 'Email is required',
                      pattern: isUpdate ? undefined : {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address"
                      }
                    })}
                    disabled={isUpdate}
                  />
                  {errors.email && !isUpdate && <div className="invalid-feedback">{errors.email.message}</div>}
                </div>

                <div className="mb-3">
                  <label htmlFor="role" className="form-label">Role</label>
                  <select
                    id="role"
                    className="form-select"
                    {...register("role", { required: true })}
                  >
                    <option value="Admin">Admin</option>
                    <option value="LimitedAccess">Limited Access User</option>
                  </select>
                </div>

                {role === 'LimitedAccess' && (
                  <div className="mb-3">
                    <label className="form-label">Permissions</label>
                    <ul className="mb-0">
                      <li>Staged Orders</li>
                      <li>Order Event Log</li>
                    </ul>
                  </div>
                )}

                {role === 'Admin' && (
                  <div className="mb-3">
                    <label className="form-label">Permissions</label>
                    <p className="mb-0">All</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={close}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={upsertUser.isLoading}>
                  {upsertUser.isLoading ? (isUpdate ? 'Updating...' : 'Adding...') : (isUpdate ? 'Update User' : 'Add User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show"></div>
    </>
  );
};

export default function Users() {
  const getUsers = useGetSiteUsers();
  const [showModal, setShowModal] = useState(false);
  const [userToUpdate, setUserToUpdate] = useState<SiteUserDto | undefined>();
  const [userToRemove, setUserToRemove] = useState<SiteUserDto | undefined>();
  const displayMessage = useDisplayMessage();

  const removeUser = useRemoveSiteUser(userToRemove?.id ?? '', {
    onSuccess: () => {
      displayMessage.success("User removed successfully");
      setUserToRemove(undefined);
      getUsers.query();
    },
    onError: (err) => displayMessage.fail("Failed to remove user: " + adminFacingErrorMessage(err))
  });

  const getPermissionsDisplay = (role: string) => {
    if (role === 'Admin') {
      return 'All';
    } else {
      return 'Staged Orders, Order Event Log';
    }
  };

  return (
    <>
      {(showModal || userToUpdate) && (
        <UserModal
          user={userToUpdate}
          onSuccess={() => {
            const isUpdate = !!userToUpdate;
            setShowModal(false);
            setUserToUpdate(undefined);
            getUsers.query();
            displayMessage.success(isUpdate ? "User updated successfully" : "User added successfully");
          }}
          close={() => {
            setShowModal(false);
            setUserToUpdate(undefined);
          }}
        />
      )}
      <Confirm
        visible={!!userToRemove}
        title={`Remove ${userToRemove?.email}?`}
        loading={removeUser.isLoading}
        body={`This will remove this user's access to the LOADPASS Administrator portal. Users can be re-added by entering their email address again.`}
        onDismiss={() => setUserToRemove(undefined)}
        onConfirm={() => removeUser.request()} />
      <form className="mx-auto w-85">
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        <Row>
          <h2 className="col">Users</h2>
          <Button
            className="w-25 col-2"
            onClick={() => setShowModal(true)}>
            Add User
          </Button>
        </Row>
        <div className="border border-dark rounded mt-4">
          <div className="m-3">
            <QueryView
              query={getUsers}
              renderData={users =>
                <>
                  <div className="row mb-2">
                    <h5 className="col-2 my-auto w-25">
                      Email Address
                    </h5>
                    <h5 className="col-2 my-auto w-25">
                      Role
                    </h5>
                    <h5 className="col-2 my-auto w-25">
                      Permissions
                    </h5>
                  </div>
                  {users
                    .sort((a, b) => a.email.localeCompare(b.email))
                    .map(user =>
                      <div key={user.id} className="row mb-2">
                        <label className="col-2 my-auto w-25">
                          {user.email}
                        </label>
                        <label className="col-2 my-auto w-25">
                          <span className={`badge ${user.role === 'Admin' ? 'bg-primary' : 'bg-secondary'}`}>
                            {user.role === 'Admin' ? 'Admin' : 'Limited Access'}
                          </span>
                        </label>
                        <label className="col-2 my-auto w-25">
                          {getPermissionsDisplay(user.role)}
                        </label>
                        <div className="col-2 my-auto w-25 dropdown text-center">
                          <button className="btn btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                            Actions
                          </button>
                          <div className="dropdown-menu">
                            <button className="dropdown-item" type="button" onClick={() => setUserToUpdate(user)}>Update</button>
                            <button className="dropdown-item" type="button" onClick={() => setUserToRemove(user)}>Remove</button>
                          </div>
                        </div>
                      </div>
                    )}
                </>
              }
            />
          </div>
        </div>
      </form>
    </>
  );
}